import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { failureOf } from '@/api/errors';
import { useCompanies, useFlexEntities, useFlexList, useFlexRow, useIncoming } from '@/api/hooks';
import type { WireFlexEntity, WireFlexRow } from '@/api/wire';
import { useMeOrThrow } from '@/app/MeContext';
import { useDepartmentLabel } from '@/app/departments';
import { Button, Skeleton } from '@/ui/primitives';
import { DecideDeal, EditFlexRecord } from './FlexEdit';
import { FactList, RecordHeader, Table, money, statusChip, when, type Column, type Fact } from '@/ui/records';
import { Refusal } from '@/ui/Refusal';
import { Incoming } from '@/directory/Records';
import { PageHeader } from '@/app/frame';
import { useTechnical } from '@/app/technical';
import { humanise } from '@/ui/words';
import { useWho } from '@/ui/who';
import { Explainer } from '@/ui/context';
import { viewerOf } from '@/directory/explainKit';
import { HIDDEN, explainFlexRow, fieldLabel, renderField } from './explain';

/**
 * The department's own records (§5): whatever its live features own —
 * Deals, Orders, Samples, Invoices — read from the tables those features
 * compiled. Nothing here knows a Deal in advance: the columns come from the
 * table, the list of kinds from the live builds, and a kind that is not
 * live is not offered. A row is what a feature wrote, and the record says
 * which feature and which version.
 */

const BLURB: Record<string, string> = {
  Deal: 'Everything in the pipeline and everything that closed.',
  Order: 'Created when a deal is won. Nothing here was typed by hand.',
  OrderItem: 'What an order carries, line by line.',
  Sample: 'What went out, to whom, and whether anyone said anything back.',
  Invoice: 'What has been billed, and who still owes.',
};

export const PLURAL: Record<string, string> = {
  Deal: 'Deals',
  Order: 'Orders',
  OrderItem: 'Order items',
  Sample: 'Samples',
  Invoice: 'Invoices',
};

export function entityTitle(entity: string): string {
  return PLURAL[entity] ?? `${entity}s`;
}

const label = fieldLabel;
const render = renderField;

export function FlexList() {
  const tech = useTechnical();
  const departmentLabel = useDepartmentLabel();
  const { entity = null } = useParams();
  const me = useMeOrThrow();
  const navigate = useNavigate();
  const list = useFlexList(me.department, entity);
  const companies = useCompanies('');
  const [creating, setCreating] = useState(false);
  const who = useWho();
  if (list.isPending) return <Skeleton rows={8} />;
  if (list.isError) return <div className="p-6"><Refusal failure={failureOf(list.error)} /></div>;
  const { entity: kind, rows } = list.data;
  const names = new Map((companies.data?.companies ?? []).map((c) => [c.id, c.displayName ?? c.legalName]));
  const fields = kind.fields.filter((f) => !HIDDEN.has(f.name) && f.name !== 'currency');
  const columns: Column<WireFlexRow>[] = fields.map((f, i) => ({
    label: label(f.name),
    primary: i === 0 && f.name !== 'company_id' ? true : f.name === 'title' || f.name === 'number' || f.name === 'product',
    num: f.type.startsWith('numeric'),
    get: (row) =>
      f.type.startsWith('numeric') && 'currency' in row
        ? money(row[f.name], row['currency'])
        : render(f.name, row[f.name], names),
    ...(f.name === 'status' ? { chip: (row: WireFlexRow) => statusChip(row['status'] as string) } : {}),
  }));
  return (
    <div className="mx-auto flex w-full max-w-[1040px] flex-col gap-5 p-6 phone:p-4">
      <PageHeader kicker={tech ? `${departmentLabel(me.department)} · ${kind.specId} v${kind.specVersion}` : departmentLabel(me.department)} title={entityTitle(kind.entity)} />
      <div>
        <Button size="row" onClick={() => setCreating(true)} data-testid="flex-new">New {kind.entity.toLowerCase()}</Button>
      </div>
      {creating && <EditFlexRecord kind={kind} onClose={() => setCreating(false)} />}
      <Table<WireFlexRow>
        title={entityTitle(kind.entity).toLowerCase()}
        blurb={BLURB[kind.entity]}
        rows={rows}
        columns={columns}
        onOpen={(row) => void navigate(`/records/${kind.entity}/${row.id}`)}
        explain={(row) => explainFlexRow(kind, row, who, viewerOf(me), { names })}
        testId={`flex-${kind.entity}`}
        emptyTitle={`No ${entityTitle(kind.entity).toLowerCase()} yet`}
        emptyBody="A row appears here when somebody records one, or when a feature writes one."
        facets={fields.some((f) => f.name === 'status') ? [{ label: 'Status', get: (r) => (r['status'] as string | null) ?? null }] : []}
      />
    </div>
  );
}

export function FlexRecord() {
  const tech = useTechnical();
  const { entity = null, id = null } = useParams();
  const me = useMeOrThrow();
  const row = useFlexRow(me.department, entity, id);
  const companies = useCompanies('');
  const incoming = useIncoming(entity && id ? { kind: 'flex', department: me.department, entity, id } : null);
  const [flow, setFlow] = useState<null | 'edit' | 'won' | 'lost'>(null);
  const who = useWho();
  if (row.isPending) return <Skeleton rows={8} />;
  if (row.isError) return <div className="p-6"><Refusal failure={failureOf(row.error)} /></div>;
  const { entity: kind, row: r } = row.data;
  const names = new Map((companies.data?.companies ?? []).map((c) => [c.id, c.displayName ?? c.legalName]));
  const title = (r['title'] ?? r['number'] ?? r['product'] ?? `A ${humanise(kind.entity).toLowerCase()} recorded ${when(r.created_at)}`) as string;
  const facts: Fact[] = kind.fields
    .filter((f) => !HIDDEN.has(f.name))
    .map((f) => [
      label(f.name),
      f.name === 'company_id' && r[f.name] ? (
        <Link to={`/companies/${String(r[f.name])}`} className="text-ink">{names.get(String(r[f.name])) ?? 'Company'}</Link>
      ) : f.name === 'deal_id' && r[f.name] ? (
        <Link to={`/records/Deal/${String(r[f.name])}`} className="text-ink">The deal</Link>
      ) : f.name === 'order_id' && r[f.name] ? (
        <Link to={`/records/Order/${String(r[f.name])}`} className="text-ink">The order</Link>
      ) : f.type.startsWith('numeric') && 'currency' in r ? (
        money(r[f.name], r['currency'])
      ) : (
        render(f.name, r[f.name], names)
      ),
    ]);
  return (
    <div className="mx-auto flex w-full max-w-[1040px] flex-col gap-6 p-6 phone:p-4">
      <RecordHeader
        kicker={tech ? `${kind.entity} · ${kind.specId} v${kind.specVersion}` : entityTitle(kind.entity)}
        title={title}
        meta={`Recorded ${when(r.created_at)}`}
        chips={r['status'] ? [statusChip(r['status'] as string)!] : []}
        actions={
          <>
            {kind.entity === 'Deal' && r['status'] !== 'won' && r['status'] !== 'lost' && (
              <>
                <Button size="row" variant="primary" onClick={() => setFlow('won')}>Won</Button>
                <Button size="row" onClick={() => setFlow('lost')}>Lost</Button>
              </>
            )}
            <Button icon="edit" size="row" onClick={() => setFlow('edit')}>Edit</Button>
          </>
        }
      />
      <Explainer startOpen label={`About this ${humanise(kind.entity).toLowerCase()}`} context={explainFlexRow(kind, r, who, viewerOf(me), { names, onPage: true })} />
      <FactList facts={facts} />
      <Incoming incoming={incoming.data} pending={incoming.isPending} />
      {flow === 'edit' && <EditFlexRecord kind={kind} row={r} onClose={() => setFlow(null)} />}
      {(flow === 'won' || flow === 'lost') && <DecideDeal kind={kind} row={r} outcome={flow} onClose={() => setFlow(null)} />}
    </div>
  );
}

/** The rail's list of record kinds: live features only. */
export function useRecordKinds(department: string): WireFlexEntity[] {
  const entities = useFlexEntities(department);
  return entities.data?.entities ?? [];
}
